import * as React from 'react';
import { useEffect, useState } from 'react';
import { CircularProgress, Button, withStyles } from '@material-ui/core';
import CustomMultiSelect from '../../../Dispatch/RouteExecution/CustomMultiSelect';
import CustomDateRangePicker from '../../../../common/MUI/DateRangePicker';
import { getDataWithQueryString } from '../../../../../api/componentAPIs';
import MESSAGES from '../../../../../appMessages';
import { compose } from 'recompose';
import { withSnackbar } from 'notistack';
import { getTerminalOptions } from '../utils';
import { RouteAuditRequest } from '../IRouteAudit';
import { withUserProfile } from '../../../../../userProfile/userProfileContext';
import { getShortenedListItemsLabel } from '../../../../common/utils';

const styles: any = (theme: any) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px'
  },
  leftSection: {
    color: theme.palette.primary.main
  },
  rightSection: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '10px'
  },
  filterInput: {
    maxWidth: 200,
    margin: '5px 0'
  },
  multiSelect: {
    width: 200,
    margin: '5px 0',
    padding: '10px 16px'
  }
});

interface DateRange {
  startDate: string | null;
  endDate: string | null;
}

type TerminalOptions = {
  label: string;
  value: string;
}[];

interface OwnProps {
  isLoading: boolean;
  getRouteAuditData: (request: RouteAuditRequest) => Promise<void>;
}

interface InjectedProps {
  classes: any;
  enqueueSnackbar: (message: string, options: any) => void;
  userData: any;
}

type FiltersProps = OwnProps & InjectedProps;

const Filters: React.FC<FiltersProps> = ({
  classes,
  enqueueSnackbar,
  userData,
  isLoading,
  getRouteAuditData
}) => {
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null
  });
  const [terminalOptions, setTerminalOptions] = useState<TerminalOptions>([]);
  const [selectedTerminals, setSelectedTerminals] = useState<TerminalOptions>([]);

  useEffect(() => {
    initializeDefaultState();
    populateTerminalOptions();
  }, []);

  // Set default state values
  const initializeDefaultState = () => {
    const today = new Date().toISOString();
    const currentTerminal = userData.userProfile.terminal;

    // set default state values
    setDateRange({
      startDate: today,
      endDate: today
    });
    setSelectedTerminals([{ label: currentTerminal.name, value: currentTerminal.code }]);

    // Call the GET api with default values
    getRouteAuditData({
      startDate: today,
      endDate: today,
      terminalCodes: [currentTerminal.code]
    });
  };

  const populateTerminalOptions = async () => {
    const terminalData = await getTerminalsList();
    const terminalOptions = getTerminalOptions(terminalData);
    setTerminalOptions(terminalOptions);
  };

  const getTerminalsList = async () => {
    try {
      const response = await getDataWithQueryString('GetAllTerminals');
      if (response.status === 200) {
        return response.data;
      }
      handleError(response);
      return [];
    } catch (error) {
      handleError(error);
      return [];
    }
  };

  const handleError = (error: any) => {
    const errorMessage =
      error.response?.data?.message || MESSAGES.TERMINAL_ACCESS.FETCH_TERMINAL_DATA_FAILURE;
    enqueueSnackbar(errorMessage, { variant: 'error' });
  };

  const handleDateRangeChange = (dateRange: DateRange) => {
    setDateRange(dateRange);
  };

  const handleTerminalsSelected = (terminals: TerminalOptions) => {
    setSelectedTerminals(terminals);
  };

  const onSubmit = () => {
    if (!isValidTerminalSelection()) {
      return;
    }
    getRouteAuditData({
      startDate: dateRange.startDate || '',
      endDate: dateRange.endDate || '',
      terminalCodes: selectedTerminals.map((terminal) => terminal.value)
    });
  };

  const isValidTerminalSelection = () => {
    if (selectedTerminals.length === 0) {
      enqueueSnackbar('Please select at least one terminal', { variant: 'error' });
      return false;
    }
    return true;
  };

  const getTerminalSelectorLabel = () => {
    const selectedTerminalCodes = selectedTerminals.map((terminal) => terminal.value);
    return getShortenedListItemsLabel({
      items: selectedTerminalCodes,
      emptyText: 'No Terminals Selected',
      showCount: 3
    });
  };

  return (
    <div className={classes.container}>
      <div className={classes.leftSection}>Route Audit</div>
      <div className={classes.rightSection} data-automationid="route-audit-filters">
        <CustomDateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          classes={{ wrapper: classes.filterInput }}
        />
        <CustomMultiSelect
          label={getTerminalSelectorLabel()}
          Options={terminalOptions}
          handleChange={handleTerminalsSelected}
          optionSelected={selectedTerminals}
          classes={{ filterButton: classes.multiSelect }}
        />
        <Button
          variant="contained"
          size="large"
          color="primary"
          classes={{ root: classes.buttonRoot }}
          onClick={onSubmit}
          data-automationid="route-audit-search">
          {isLoading ? (
            <CircularProgress size={24} classes={{ root: classes.circularProgress }} />
          ) : (
            'Search'
          )}
        </Button>
      </div>
    </div>
  );
};

export default compose(
  withStyles(styles),
  withSnackbar,
  withUserProfile
)(Filters) as React.ComponentType<OwnProps>;
